import type { NextTaskKey } from '@/types/api';

/**
 * 全流程展示状态 code（与后端 flowDisplayStatus / 列表角标一致）
 * 中文文案见 TASK_ASSIGNMENT_CARD_STATUS_NAMES（设计稿统一维护）
 */
export enum TaskAssignmentCardStatus {
  PENDING = 'PENDING',
  PENDING_FAIR_PRICE_REPLY = 'PENDING_FAIR_PRICE_REPLY',
  PENDING_FAIR_PRICE_AUDIT = 'PENDING_FAIR_PRICE_AUDIT',
  FAIR_PRICE_AUDIT_REJECTED = 'FAIR_PRICE_AUDIT_REJECTED',
  PENDING_TOTAL_PRICE_INPUT = 'PENDING_TOTAL_PRICE_INPUT',
  PRICE_REVIEWING = 'PRICE_REVIEWING',
  PENDING_WITHDRAW_APPROVAL = 'PENDING_WITHDRAW_APPROVAL',
  PRICE_REJECTED = 'PRICE_REJECTED',
  REPORT_TO_SUBMIT = 'REPORT_TO_SUBMIT',
  REPORT_REVIEWING = 'REPORT_REVIEWING',
  REPORT_REJECTED = 'REPORT_REJECTED',
  FINISHED = 'FINISHED',
}

/** 与后端 flowDisplayStatus 同义，便于 API 层引用 */
export type FlowDisplayStatus = TaskAssignmentCardStatus;

/** 设计稿统一状态文案 */
export const TASK_ASSIGNMENT_CARD_STATUS_NAMES: Record<TaskAssignmentCardStatus, string> = {
  [TaskAssignmentCardStatus.PENDING]: '待补充',
  [TaskAssignmentCardStatus.PENDING_FAIR_PRICE_REPLY]: '待回复公允价',
  [TaskAssignmentCardStatus.PENDING_FAIR_PRICE_AUDIT]: '待公允价审批',
  [TaskAssignmentCardStatus.FAIR_PRICE_AUDIT_REJECTED]: '公允价审批驳回',
  [TaskAssignmentCardStatus.PENDING_TOTAL_PRICE_INPUT]: '待提交复核价',
  [TaskAssignmentCardStatus.PRICE_REVIEWING]: '偏离度审批中',
  [TaskAssignmentCardStatus.PENDING_WITHDRAW_APPROVAL]: '撤回审批中',
  [TaskAssignmentCardStatus.PRICE_REJECTED]: '偏离度审批驳回',
  [TaskAssignmentCardStatus.REPORT_TO_SUBMIT]: '待提交报告审核',
  [TaskAssignmentCardStatus.REPORT_REVIEWING]: '报告审核中',
  [TaskAssignmentCardStatus.REPORT_REJECTED]: '报告审批驳回',
  [TaskAssignmentCardStatus.FINISHED]: '已完结',
};

export interface CardDisplayStatus {
  badgeEnum: TaskAssignmentCardStatus;
  badgeLabel: string;
}

export const CARD_BADGE_COLORS: Record<TaskAssignmentCardStatus, string> = {
  [TaskAssignmentCardStatus.PENDING]: 'default',
  [TaskAssignmentCardStatus.PENDING_FAIR_PRICE_REPLY]: 'default',
  [TaskAssignmentCardStatus.PENDING_FAIR_PRICE_AUDIT]: 'default',
  [TaskAssignmentCardStatus.FAIR_PRICE_AUDIT_REJECTED]: 'warning',
  [TaskAssignmentCardStatus.PENDING_TOTAL_PRICE_INPUT]: 'default',
  [TaskAssignmentCardStatus.PRICE_REVIEWING]: 'processing',
  [TaskAssignmentCardStatus.PENDING_WITHDRAW_APPROVAL]: 'processing',
  [TaskAssignmentCardStatus.PRICE_REJECTED]: 'warning',
  [TaskAssignmentCardStatus.REPORT_TO_SUBMIT]: 'warning',
  [TaskAssignmentCardStatus.REPORT_REVIEWING]: 'processing',
  [TaskAssignmentCardStatus.REPORT_REJECTED]: 'error',
  [TaskAssignmentCardStatus.FINISHED]: 'success',
};

const ALL_FLOW_DISPLAY_STATUSES: TaskAssignmentCardStatus[] = [
  TaskAssignmentCardStatus.PENDING,
  TaskAssignmentCardStatus.PENDING_FAIR_PRICE_REPLY,
  TaskAssignmentCardStatus.PENDING_FAIR_PRICE_AUDIT,
  TaskAssignmentCardStatus.FAIR_PRICE_AUDIT_REJECTED,
  TaskAssignmentCardStatus.PENDING_TOTAL_PRICE_INPUT,
  TaskAssignmentCardStatus.PRICE_REVIEWING,
  TaskAssignmentCardStatus.PENDING_WITHDRAW_APPROVAL,
  TaskAssignmentCardStatus.PRICE_REJECTED,
  TaskAssignmentCardStatus.REPORT_TO_SUBMIT,
  TaskAssignmentCardStatus.REPORT_REVIEWING,
  TaskAssignmentCardStatus.REPORT_REJECTED,
  TaskAssignmentCardStatus.FINISHED,
];

/** 任务统计搜索 / Tab 下拉选项 */
export const FLOW_DISPLAY_STATUS_SEARCH_OPTIONS: Array<{ value: TaskAssignmentCardStatus; label: string }> =
  ALL_FLOW_DISPLAY_STATUSES.map((value) => ({
    value,
    label: TASK_ASSIGNMENT_CARD_STATUS_NAMES[value],
  }));

export function getFlowDisplayStatusSearchValueEnum(): Record<string, { text: string }> {
  return Object.fromEntries(
    FLOW_DISPLAY_STATUS_SEARCH_OPTIONS.map((o) => [o.value, { text: o.label }]),
  );
}

/** 筛选区「全部」选项值（不传 flowDisplayStatus 给后端） */
export const FLOW_DISPLAY_STATUS_ALL = 'ALL';

export function getFlowDisplayStatusFilterValueEnum(): Record<string, { text: string }> {
  return {
    [FLOW_DISPLAY_STATUS_ALL]: { text: '全部' },
    ...getFlowDisplayStatusSearchValueEnum(),
  };
}

export function isFlowDisplayStatusAll(value: unknown): boolean {
  return value === undefined || value === null || value === '' || value === FLOW_DISPLAY_STATUS_ALL;
}

function toCardStatus(status: TaskAssignmentCardStatus): CardDisplayStatus {
  return {
    badgeEnum: status,
    badgeLabel: TASK_ASSIGNMENT_CARD_STATUS_NAMES[status],
  };
}

function isKnownFlowDisplayStatus(code: unknown): code is TaskAssignmentCardStatus {
  return (
    typeof code === 'string'
    && Object.values(TaskAssignmentCardStatus).includes(code as TaskAssignmentCardStatus)
  );
}

/** 撤回审批进行中时的列表文案 */
export const PENDING_SUPPLEMENT_WITHDRAW_APPROVAL_LABEL = '撤回审批中';

/**
 * 价格复核 / 展示状态为撤回审批中。
 * 此时列表文案为「撤回审批中」，且不允许进入编辑。
 * 不限定主状态必须是「待补充」，报告驳回后发起撤回同样适用。
 */
export function isPendingSupplementWithdrawApproval(task: Record<string, any>): boolean {
  return (
    task.priceReviewStatus === TaskAssignmentCardStatus.PENDING_WITHDRAW_APPROVAL
    || task.flowDisplayStatus === TaskAssignmentCardStatus.PENDING_WITHDRAW_APPROVAL
  );
}

/**
 * 列表任务状态角标：返回 badgeEnum、badgeLabel，仅用于展示。
 * 撤回审批中优先于 flowDisplayStatus；否则优先使用接口 flowDisplayStatus，再按 status 等字段推算。
 * 列表行必须原样带回 flowDisplayStatus / priceReviewStatus，否则会落到 status==16 的「报告审批驳回」兜底。
 */
export function getCardDisplayStatus(task: Record<string, any>): CardDisplayStatus {
  if (isPendingSupplementWithdrawApproval(task)) {
    return {
      badgeEnum: TaskAssignmentCardStatus.PENDING_WITHDRAW_APPROVAL,
      badgeLabel: PENDING_SUPPLEMENT_WITHDRAW_APPROVAL_LABEL,
    };
  }
  return resolveCardDisplayStatus(task);
}

function resolveCardDisplayStatus(task: Record<string, any>): CardDisplayStatus {
  if (isKnownFlowDisplayStatus(task.flowDisplayStatus)) {
    return toCardStatus(task.flowDisplayStatus);
  }

  const taskStatus = task.status ?? 0;
  const priceReviewStatus = task.priceReviewStatus as NextTaskKey | string | undefined;
  const isPriceRejected = task.isRejected === true;
  const customerTaskStatus = task.customerTaskStatus;

  if (
    !isPriceRejected &&
    (priceReviewStatus === 'FIRST_REVIEWING' || priceReviewStatus === 'SECOND_REVIEWING')
  ) {
    return toCardStatus(TaskAssignmentCardStatus.PRICE_REVIEWING);
  }

  if (priceReviewStatus === 'PENDING_FAIR_PRICE_REPLY' && (taskStatus == 9 || taskStatus == 14 || taskStatus == 0)) {
    return toCardStatus(TaskAssignmentCardStatus.PENDING_FAIR_PRICE_REPLY);
  }

  if (priceReviewStatus === 'PENDING_FAIR_PRICE_AUDIT' && (taskStatus == 9 || taskStatus == 14 || taskStatus == 0)) {
    return toCardStatus(TaskAssignmentCardStatus.PENDING_FAIR_PRICE_AUDIT);
  }

  if (priceReviewStatus === 'PENDING_TOTAL_PRICE_INPUT' && (taskStatus == 9 || taskStatus == 14 || taskStatus == 0)) {
    return toCardStatus(TaskAssignmentCardStatus.PENDING_TOTAL_PRICE_INPUT);
  }

  if (isPriceRejected) {
    return toCardStatus(TaskAssignmentCardStatus.PRICE_REJECTED);
  }

  if (taskStatus == 15) {
    return toCardStatus(TaskAssignmentCardStatus.REPORT_REVIEWING);
  }

  if (taskStatus == 16) {
    return toCardStatus(TaskAssignmentCardStatus.REPORT_REJECTED);
  }

  if (
    (priceReviewStatus === 'REPLIED' || priceReviewStatus === 'CONFIRMED') &&
    (!!taskStatus && taskStatus < 15 || taskStatus == 0)
  ) {
    return toCardStatus(TaskAssignmentCardStatus.REPORT_TO_SUBMIT);
  }

  if (customerTaskStatus === 'PENDING_ACCEPT' || customerTaskStatus === 'IN_PROGRESS') {
    return toCardStatus(TaskAssignmentCardStatus.PENDING);
  }

  if (taskStatus == 99) {
    return toCardStatus(TaskAssignmentCardStatus.FINISHED);
  }

  return toCardStatus(TaskAssignmentCardStatus.PENDING);
}
